import { createSlice } from '@reduxjs/toolkit';
import { EUserRolesUI, TGetUserDataUI } from 'data/types/user.types';
import { userApi } from 'redux/apiSlices/user.slice';
import { TCurrentAuthUserUI } from 'data/types/amplify.types';
import { authApi } from 'redux/apiSlices/auth.slice';
import { clearSessionStorage } from 'helpers/clearSessionStorage';

export type TAuthState = TGetUserDataUI & { amplifyUser: null | TCurrentAuthUserUI };
const initialState: TAuthState = {
  userType: null,
  users: null,
  impersonateOn: false,
  amplifyUser: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '') : null,
};

const usersSlice = createSlice({
  name: 'usersSlice',
  initialState,
  reducers: {
    resetUsersState: () => ({
      userType: null,
      users: null,
      impersonateOn: null,
      amplifyUser: null,
    }),
  },
  extraReducers: (builder) => {
    builder.addMatcher(userApi.endpoints.getUsers.matchFulfilled, (state, action) => {
      if (action.payload) {
        return {
          ...state,
          userType: action.payload.userType,
          users: action.payload.users,
          impersonateOn: action.payload.impersonateOn,
        };
      }
      return state;
    });
    builder.addMatcher(userApi.endpoints.impersonateUser.matchFulfilled, (state, action) => {
      if (action.payload && state.users) {
        localStorage.setItem('lastImpersonatedUserId', action.payload.id);

        return {
          ...state,
          userType: action.payload.type,
          users: {
            [EUserRolesUI.ADMIN]: null,
            [EUserRolesUI.PROPERTY_MANAGER]: null,
            [EUserRolesUI.LANDLORD]: null,
            [action.payload.type]: action.payload,
          },
          impersonateOn: true,
        };
      }
      return state;
    });
    builder.addMatcher(userApi.endpoints.deleteImpersonation.matchFulfilled, (state) => {
      if (state.users) {
        localStorage.removeItem('lastImpersonatedUserId');

        return {
          ...state,
          userType: EUserRolesUI.ADMIN,
          users: {
            [EUserRolesUI.ADMIN]: null,
            [EUserRolesUI.PROPERTY_MANAGER]: null,
            [EUserRolesUI.LANDLORD]: null,
          },
          impersonateOn: false,
        };
      }
      return state;
    });
    builder.addMatcher(authApi.endpoints.signIn.matchFulfilled, (state, action) => {
      if (action.payload) {
        return {
          ...state,
          amplifyUser: action.payload,
        };
      }
      return state;
    });
    builder.addMatcher(authApi.endpoints.signOut.matchFulfilled, () => {
      clearSessionStorage();
      return {
        userType: null,
        users: null,
        impersonateOn: null,
        amplifyUser: null,
      };
    });
    builder.addMatcher(authApi.endpoints.setNewPassword.matchFulfilled, (state, action) => {
      if (action.payload) {
        return {
          ...state,
          amplifyUser: action.payload,
        };
      }
      return state;
    });
  },
});

export default usersSlice;
